/**
 * PublicShell — the ground every page a studio's CLIENT opens stands on.
 *
 * PORTAL-GOOGIE-2. `SignDocument` got this treatment first (PORTAL-GOOGIE-1)
 * and the lesson generalised immediately: the thing wrong with the public
 * pages was not their layout, it was that they rendered OUTSIDE `.googie`.
 * That one scope is where `--primary` becomes the signature blue, where the
 * fonts come from, and where every `--g-*` token the redesign is built on
 * exists. A public page without it falls back to index.css's periwinkle and
 * a flat `bg-background`, which is exactly what Rennan was looking at.
 *
 * So this is deliberately thin. It is a scope, a ground, and nothing else —
 * no header, no nav, no opinions about what goes inside. Pages keep their own
 * layout; they just stop being orphans of the design system.
 *
 * WHY NOT `GoogieScreen`
 *
 * That is the studio's shell: the nav rail, the time-of-day sky, the whole
 * app around it. A client opening an invoice or a gallery delivery should get
 * Orbit's language, not Orbit's furniture — they do not have an account and
 * there is nowhere for them to navigate to.
 *
 * WHY NIGHT, ALWAYS
 *
 * The shell's sky moves through the day, which is lovely behind a dashboard
 * and wrong here. Dawn and dusk put light-peach stops behind the middle of a
 * long page, and these are pages that get read carefully, printed and filed.
 * A client also has no idea Orbit has a time-of-day sky, so a document that
 * looks different at 7pm than it did at 10am just looks unreliable.
 */
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export function PublicShell({
  children,
  className,
  /** Set when the page paints its own ground (a gallery, a site preview). */
  bare = false,
}: {
  children: ReactNode;
  className?: string;
  bare?: boolean;
}) {
  return (
    <div
      className={cn('googie dark relative min-h-screen', className)}
      data-g-mode="dark"
      data-public-doc
    >
      {!bare && (
        <>
          <div
            aria-hidden
            data-g-sky
            className="pointer-events-none fixed inset-0 print:hidden"
            style={{
              background:
                'linear-gradient(180deg,#0A0F1F 0%,#111832 38%,#150F28 62%,#0B0B0C 100%)',
            }}
          />
          {/* The same soft glow the app wears behind its top-right corner.
              At 14% it is a warmth rather than a light source — enough that
              the page is not a flat rectangle of ink. */}
          <div
            aria-hidden
            data-g-sky
            className="pointer-events-none fixed right-[-10%] top-[-18%] h-[620px] w-[620px] print:hidden"
            style={{
              background: 'radial-gradient(circle,#8FB4FF 0%,transparent 60%)',
              opacity: 0.14,
            }}
          />
        </>
      )}
      <div className="relative">{children}</div>
    </div>
  );
}

export default PublicShell;
